import { Grid, Typography, Slider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TikTokTouchpointStateKeys } from 'pages/calculator/MediaPlanning/components/Touchpoints/TikTokTouchpoint/interfaces';
import { UseMutateFunction } from 'react-query';
import convertDecimalToPercentValue from '../../../../Plan/Countries/utils/convertDecimalToPercentValue';
import { TiktokTargetGroup, TiktokTouchpointSetup } from '../../interfaces';
import usePlanStore from '../../../../../../../store/planStore';
import { ObjectSelect } from '../../../../common/ObjectSelect/ObjectSelect';
import { isRecommendedTargetGroupMissing } from '../../../../../../../shared/utils';
import GoldenRuleStatusBox from '../../MetaTouchpoint/GoldenRuleBox/GoldenRuleStatusBox';
import GoldenRuleBox from '../../MetaTouchpoint/GoldenRuleBox/GoldenRuleBox';
import renderSaturation from './utils/renderSaturation';
import getSaturationNullish from '../../utils/getSaturationNullish';
import getSaturationMinEqualToMax from '../../utils/getSaturationMinEqualToMax';
import { ApiResponse } from '../../../../../../../api/services/mediaPlanning/types';
import HelperTooltip from '../../../../../../../shared/HelperTooltip/HelperTooltip';
import linksConfig from '../../../../../../../config/linking';

interface TargetGroupSelectorsProps {
  targetGroupSaturation: number;
  handleChangeStateValues: (key: TikTokTouchpointStateKeys, value: any) => void;
  minMaxSelectorsValues?: TiktokTouchpointSetup;
  fetchTiktokConfig: UseMutateFunction<
    ApiResponse<TiktokTouchpointSetup>,
    any,
    TiktokTargetGroup | undefined,
    unknown
  >;
}

function TargetGroupSelectors({
  targetGroupSaturation,
  handleChangeStateValues,
  minMaxSelectorsValues,
  fetchTiktokConfig,
}: TargetGroupSelectorsProps) {
  const { t } = useTranslation();

  const { countryForms, selectedCountry } = usePlanStore();

  const { tiktokConfig } = countryForms[selectedCountry!];
  const isTargetGroupsError = tiktokConfig?.error && !tiktokConfig?.targetGroup;
  const isRecommendedGroupMissing =
    !tiktokConfig?.targetGroup &&
    isRecommendedTargetGroupMissing(
      tiktokConfig?.recommendedTargetGroup,
      tiktokConfig?.targetGroups,
      (group) => group.targetGroup
    );

  const getTargetGroupErrorMessage = () => {
    if (isRecommendedGroupMissing) {
      return t('mediaPlanning.errors.recommendedTargetGroupMissing');
    }
    if (isTargetGroupsError) {
      return t('mediaPlanning.errors.targetGroup');
    }
    return t('mediaPlanning.errors.goldenRuleTT');
  };

  return (
    <Grid container>
      <Grid item xs={3} sx={{ pr: '24px' }}>
        <ObjectSelect<TiktokTargetGroup>
          value={
            tiktokConfig?.targetGroup
              ? {
                  value: tiktokConfig.targetGroup,
                  name:
                    tiktokConfig.targetGroup.targetGroup ===
                    minMaxSelectorsValues?.recommendedTargetGroup
                      ? `${tiktokConfig.targetGroup.targetGroup} (recommended)`
                      : tiktokConfig.targetGroup.targetGroup ?? undefined,
                }
              : null
          }
          onChange={(item) => {
            handleChangeStateValues('targetGroup', item.value);
            fetchTiktokConfig(item.value);
          }}
          items={
            countryForms[selectedCountry!].tiktokConfig?.targetGroups?.map(
              (group) => ({
                value: group,
                name:
                  group.targetGroup ===
                  minMaxSelectorsValues?.recommendedTargetGroup
                    ? `${group.targetGroup} (recommended)`
                    : group.targetGroup,
              })
            ) || []
          }
          getOptionKey={(item) => item.targetGroup}
          selectName={t('mediaPlanning.targetGroup')}
          placeholder={t('mediaPlanning.placeholders.targetGroup')}
          labelId="target-group"
          error={
            tiktokConfig?.isGoldenRuleMet === false ||
            isTargetGroupsError ||
            isRecommendedGroupMissing
          }
          errorMessage={getTargetGroupErrorMessage()}
        />
      </Grid>
      {tiktokConfig?.isGoldenRuleMet === false ? (
        <GoldenRuleBox />
      ) : (
        <>
          <Grid
            item
            xs={3}
            sx={{ pr: '24px', display: 'flex', flexDirection: 'column' }}
          >
            <Typography variant="caption" lineHeight={1}>
              {renderSaturation(
                t('mediaPlanning.targetGroupSaturation'),
                targetGroupSaturation,
                {
                  min: minMaxSelectorsValues?.saturationMin,
                  max: minMaxSelectorsValues?.saturationMax,
                  default: minMaxSelectorsValues?.saturationDefault,
                }
              )}
            </Typography>
            <Slider
              valueLabelDisplay="auto"
              value={targetGroupSaturation ?? 0}
              step={0.01}
              min={minMaxSelectorsValues?.saturationMin ?? 0}
              max={minMaxSelectorsValues?.saturationMax ?? 1}
              slots={{
                track: getSaturationMinEqualToMax(minMaxSelectorsValues)
                  ? () => null
                  : undefined,
              }}
              marks={
                minMaxSelectorsValues?.saturationMin !== null &&
                minMaxSelectorsValues?.saturationMax !== null
                  ? [
                      {
                        value: minMaxSelectorsValues?.saturationMin ?? 0,
                        label: convertDecimalToPercentValue(
                          minMaxSelectorsValues?.saturationMin
                        ),
                      },
                      ...(minMaxSelectorsValues?.saturationMin !==
                      minMaxSelectorsValues?.saturationMax
                        ? [
                            {
                              value: minMaxSelectorsValues?.saturationMax ?? 1,
                              label: convertDecimalToPercentValue(
                                minMaxSelectorsValues?.saturationMax
                              ),
                            },
                          ]
                        : []),
                    ]
                  : []
              }
              valueLabelFormat={(value) =>
                `${convertDecimalToPercentValue(value).toFixed(0)}%`
              }
              onChange={(_, newValue) =>
                handleChangeStateValues('saturation', +newValue)
              }
              disabled={
                getSaturationMinEqualToMax(minMaxSelectorsValues) ||
                getSaturationNullish(minMaxSelectorsValues)
              }
            />
          </Grid>
          <Grid item xs={6} display="flex" alignItems="center" gap={1}>
            {tiktokConfig?.targetGroup?.targetSizePopulation != null && (
              <GoldenRuleStatusBox
                reach={
                  tiktokConfig?.saturationMin
                    ? tiktokConfig.targetGroup.targetSizePopulation *
                      (tiktokConfig?.saturation ?? 0)
                    : 0
                }
                grReach={tiktokConfig?.grReach}
              />
            )}
            <HelperTooltip
              tooltipData={linksConfig.seeGoldenRules}
              sx={{ marginLeft: '12px', alignSelf: 'flex-start' }}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
}

export default TargetGroupSelectors;
